import lodashGet from "lodash.get";
import lodashSet from "lodash.set";
import { ZendeskStatus } from "./types";

const ZENDESK_REQUESTS_KEY = "zendesk-requests";

function getObject(localStorageKey): { [ZENDESK_REQUESTS_KEY]?: string[] } {
  const value = window.localStorage.getItem(localStorageKey);

  if (value === null) {
    return {};
  }

  let obj = {};

  try {
    obj = JSON.parse(value);
  } catch (_e) {
    console.error(
      `Failed to JSON.parse '${localStorageKey}' from local storage: ${value}`,
    );

    return {};
  }

  return obj;
}

export type GetRequestsParam = {
  userEmail: string;
  accountNumber: string;
  requestType: string;
};

function getRequestsPath({
  userEmail,
  accountNumber,
  requestType,
}: GetRequestsParam): string[] {
  // _ is needed because otherwise a number (even if it's a string) is converted to array item access.
  return [userEmail, `_${accountNumber}`, requestType];
}

/*
  Example structure:
  {
	  // user email
	  "david@gmail.com": {
		  // account number (prefixed with _)
		  "_6015766622135677": {
			  closeAccount: [460714, 460892]
	  	}
	  }
  }
*/
export function storeRequest({
  userEmail,
  accountNumber,
  requestType,
  ticketId,
}: GetRequestsParam & { ticketId: string }): void {
  const obj = getObject(ZENDESK_REQUESTS_KEY);
  const path = getRequestsPath({ userEmail, accountNumber, requestType });
  const currentTicketIds = lodashGet(obj, path) ?? [];
  const newObj = lodashSet(obj, path, [...currentTicketIds, ticketId]);

  window.localStorage.setItem(ZENDESK_REQUESTS_KEY, JSON.stringify(newObj));
}

export function getRequests({
  userEmail,
  accountNumber,
  requestType,
}: GetRequestsParam): ZendeskStatus[] {
  const obj = getObject(ZENDESK_REQUESTS_KEY);
  const path = getRequestsPath({ userEmail, accountNumber, requestType });

  return lodashGet(obj, path) ?? [];
}

// See: https://developer.zendesk.com/rest_api/docs/support/tickets#json-format
const ZENDESK_TICKET_STATUSES: ZendeskStatus[] = [
  "new",
  "open",
  "pending",
  "hold",
  "solved",
  "closed",
];

export function getLeastAdvancedStatus(
  statuses: ZendeskStatus[],
): ZendeskStatus {
  const indices = statuses
    .map((status) => ZENDESK_TICKET_STATUSES.indexOf(status))
    .filter((index) => index !== -1);
  const minIndex = Math.min(...indices);

  return ZENDESK_TICKET_STATUSES[minIndex];
}
